// The client side of /api/personas (BUILD-BRIEF-R2 §10).
//
// Two kinds of persona and two verbs that must never be confused:
//   · `synthesise` starts a RUN. It writes no persona — the run files a `persona` proposal and
//     the operator holds it in Review. The answer is a run ref, and the UI says so.
//   · `createSegmentPersona` writes a HELD row on the spot, because the operator is its author.
import { qs, request } from './client.js';
import type { ListResponse, Persona, PersonaStatus, PersonaSubjectKind, SegmentPersonaInput } from '../../shared/types.js';

export interface ListPersonaParams {
  subject_kind?: PersonaSubjectKind;
  subject_id?: number;
  status?: PersonaStatus;
  /** Include superseded versions — the history, not the answer. */
  all?: boolean;
  limit?: number;
}

export function listPersonas(params: ListPersonaParams = {}): Promise<ListResponse<Persona>> {
  return request(`/personas${qs({ ...params })}`);
}

export function getPersona(id: number): Promise<Persona> {
  return request(`/personas/${id}`);
}

/** The persona that currently stands for a person: held first, then the newest proposed one. */
export async function personaFor(personId: number): Promise<Persona | null> {
  const answer = await listPersonas({ subject_kind: 'person', subject_id: personId });
  const held = answer.items.find((p) => p.status === 'held');
  return held ?? answer.items[0] ?? null;
}

export interface SynthesisStart {
  run_id: number;
  run_ref: string;
  skill: string;
  proposal_ref: string | null;
}

/** Starts a `persona_synth` run. Nothing is written until the proposal it files is held. */
export function synthesise(personId: number): Promise<SynthesisStart> {
  return request('/personas/synthesise', { method: 'POST', body: JSON.stringify({ person_id: personId }) });
}

/** A SEGMENT persona only. A person persona arrives as a `persona` proposal and is held in
 *  Review — there is no verb here that writes one directly. */
export function createSegmentPersona(input: SegmentPersonaInput): Promise<Persona> {
  return request('/personas/segments', { method: 'POST', body: JSON.stringify(input) });
}

export function updatePersona(id: number, patch: Partial<SegmentPersonaInput> & { status?: PersonaStatus }): Promise<Persona> {
  return request(`/personas/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export interface LookalikeResult {
  requested: number;
  proposed: number;
  capped: boolean;
  cap: number;
  used_today: number;
  already_known: number;
  snapshots_requested: number;
  unused_criteria: string[];
  persona: { id: number; name: string | null; version: number };
}

export function lookalikes(personaId: number): Promise<LookalikeResult> {
  return request(`/personas/${personaId}/lookalikes`, { method: 'POST' });
}

// ── candidates ──────────────────────────────────────────────────────────────

/** A person named by evidence who is not on the record — from a proposal nobody has held, or
 *  from a `persons` row sitting at status 'candidate'. */
export interface CandidateRow {
  key: string;
  person_id: number | null;
  ref: string | null;
  name: string;
  role_title: string | null;
  organisation_name: string | null;
  linkedin_url: string | null;
  proposal_id: number | null;
  proposal_ref: string | null;
  proposal_principal: string | null;
  persona_id: number | null;
  persona_name: string | null;
  quote: string | null;
  evidence_ref: string | null;
  created_at: string;
  note: string;
}

export function listCandidates(): Promise<ListResponse<CandidateRow>> {
  return request('/personas/candidates');
}

/**
 * Promote one candidate. A candidate WITH a proposal is promoted by holding the proposal — one
 * door (P1), and the applier writes the person. A candidate that is only a `persons` row at
 * status 'candidate' has no proposal to hold, so its status is flipped directly, which is the
 * operator writing to their own record.
 */
export async function promote(candidate: Pick<CandidateRow, 'proposal_id' | 'person_id'>): Promise<void> {
  if (candidate.proposal_id) {
    await request(`/proposals/${candidate.proposal_id}/hold`, { method: 'POST' });
    return;
  }
  if (candidate.person_id) {
    await request(`/persons/${candidate.person_id}`, { method: 'PATCH', body: JSON.stringify({ status: 'held' }) });
    return;
  }
  throw new Error('This candidate has neither a proposal to hold nor a row to promote.');
}
